import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter,
  Download,
  Trash2,
  Heart,
  MoreVertical,
  Grid,
  List,
  Calendar,
  FileImage,
  Loader2,
  Copy,
  RefreshCw,
  Star,
  Maximize2,
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Scissors
} from 'lucide-react';
import Button from '../../components/Button';
import { libraryService, Mockup } from '../../services/libraryService';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getErrorMessage } from '../../utils/errorHandler';

const Library: React.FC = () => {
  const [mockups, setMockups] = useState<Mockup[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filter, setFilter] = useState<'all' | 'favorites'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMockup, setSelectedMockup] = useState<Mockup | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [previewMockup, setPreviewMockup] = useState<Mockup | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [removingBackground, setRemovingBackground] = useState<string | null>(null);
  const itemsPerPage = 12;

  // Initial load
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setMockups([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    loadMockups();
  }, [user, authLoading]);

  // Load when filters change
  useEffect(() => {
    // Skip the initial mount
    if (mockups.length > 0 || totalCount > 0 || loadError) {
      loadMockups();
    }
  }, [filter, searchQuery, page]);

  // Keyboard shortcuts for preview modal
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!previewMockup) return;

      switch (e.key) {
        case 'Escape':
          setPreviewMockup(null);
          setZoomLevel(1);
          setRotation(0);
          break;
        case '+':
        case '=':
          setZoomLevel(prev => Math.min(3, prev + 0.25));
          break;
        case '-':
        case '_':
          setZoomLevel(prev => Math.max(0.5, prev - 0.25));
          break;
        case 'r':
        case 'R':
          setRotation(prev => (prev + 90) % 360);
          break;
        case 'd':
        case 'D':
          handleDownload(previewMockup);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [previewMockup]);

  const loadMockups = async () => {
    let loadTimeout: NodeJS.Timeout;
    
    try {
      setLoading(true);
      setLoadError(null);
      
      // Set a timeout to prevent infinite loading
      loadTimeout = setTimeout(() => {
        setLoading(false);
        setLoadError('Loading took too long. Please refresh the page.');
        console.warn('Library loading timeout');
      }, 10000); // 10 second timeout
      
      const { mockups: data, count, error } = await libraryService.getUserMockups({
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        favoritesOnly: filter === 'favorites',
        searchQuery: searchQuery || undefined
      });

      clearTimeout(loadTimeout);

      if (error) {
        console.error('Error loading mockups:', error);
        const errorMessage = getErrorMessage(error);
        setLoadError(errorMessage);
        showNotification('error', 'Failed to load mockups', errorMessage);
      } else {
        setMockups(data);
        setTotalCount(count);
        setLoadError(null);
        console.log('Loaded mockups:', data.length);
      }
    } catch (error) {
      console.error('Failed to load mockups:', error);
      const errorMessage = getErrorMessage(error);
      setLoadError(errorMessage);
      showNotification('error', 'Failed to load mockups', errorMessage);
    } finally {
      clearTimeout(loadTimeout!);
      setLoading(false);
    }
  };

  const handleDownload = async (mockup: Mockup) => {
    const result = await libraryService.downloadMockup(mockup);
    if (!result.success) {
      showNotification('error', 'Download failed', result.error || 'Failed to download mockup');
    } else {
      showNotification('success', 'Download started', `${mockup.name} is being downloaded`);
    }
  };

  const handleToggleFavorite = async (mockup: Mockup) => {
    const result = await libraryService.toggleFavorite(mockup.id);
    if (result.success) {
      loadMockups();
      showNotification('success', 
        mockup.is_favorite ? 'Removed from favorites' : 'Added to favorites',
        `${mockup.name} has been updated`
      );
    } else {
      showNotification('error', 'Failed to update favorite', result.error || 'An error occurred');
    }
  };

  const handleDelete = async (mockup: Mockup) => {
    if (!window.confirm(`Are you sure you want to delete "${mockup.name}"?`)) {
      return;
    }

    setIsDeleting(mockup.id);
    const result = await libraryService.deleteMockup(mockup.id);
    
    if (result.success) {
      loadMockups();
      showNotification('success', 'Mockup deleted', `${mockup.name} has been permanently deleted`);
    } else {
      showNotification('error', 'Delete failed', result.error || 'Failed to delete mockup');
    }
    setIsDeleting(null);
  };

  const handleRegenerateVariation = (mockup: Mockup) => {
    // Store mockup settings and navigate to customize page
    sessionStorage.setItem('regenerateFrom', JSON.stringify({
      template: mockup.template_name,
      settings: mockup.settings
    }));
    // Navigate to templates to start new flow with same settings
    window.location.href = '/dashboard/mockups';
  };

  const handleRemoveBackground = async (mockup: Mockup) => {
    if (!mockup.id || removingBackground) return;
    
    setRemovingBackground(mockup.id);
    
    try {
      const result = await libraryService.removeBackground(mockup.id, mockup.mockup_url);
      
      if (result.error) {
        showNotification('error', 'Background removal failed', result.error);
      } else if (result.mockup) {
        // Open the result in a new window
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        window.open(`${baseUrl}${result.mockup.url}`, '_blank');
        showNotification('success', 'Background removed successfully!', 'Opening in new window...');
      }
    } catch (error: any) {
      console.error('Background removal error:', error);
      showNotification('error', 'Background removal failed', 'Failed to remove background');
    } finally {
      setRemovingBackground(null);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && mockups.length === 0 && !loadError) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-brand-purple" />
          <p className="mt-4 text-gray-600">Loading your mockups...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="mb-4 p-3 bg-red-50 rounded-full inline-block">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Unable to Load Mockups</h3>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-h2 font-heading text-text-dark mb-2">
              My Library
            </h1>
            <p className="text-body text-body-grey">
              All your generated mockups in one place
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-body-grey">
            <span>{totalCount} mockups total</span>
            <span>•</span>
            <span>{mockups.filter(m => m.is_favorite).length} favorites</span>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search mockups..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as 'all' | 'favorites');
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple"
            >
              <option value="all">All Mockups</option>
              <option value="favorites">Favorites Only</option>
            </select>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors border-l border-gray-300`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mockups Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {mockups.map((mockup) => (
            <div key={mockup.id} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group">
              {/* Thumbnail */}
              <div className="aspect-[4/3] relative overflow-hidden rounded-t-xl bg-gray-100">
                <img 
                  src={mockup.thumbnail_url || mockup.mockup_url} 
                  alt={mockup.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewMockup(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Preview"
                    >
                      <Maximize2 className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleDownload(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="Download"
                    >
                      <Download className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleToggleFavorite(mockup)}
                      className={`p-2 bg-white rounded-full hover:bg-gray-100 transition-colors ${
                        mockup.is_favorite ? 'text-red-500' : 'text-gray-700'
                      }`}
                      title="Toggle Favorite"
                    >
                      <Heart className={`w-5 h-5 ${mockup.is_favorite ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleRemoveBackground(mockup)}
                      disabled={removingBackground === mockup.id}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove Background"
                    >
                      {removingBackground === mockup.id ? (
                        <Loader2 className="w-5 h-5 text-gray-700 animate-spin" />
                      ) : (
                        <Scissors className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedMockup(mockup)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                      title="More Options"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-700" />
                    </button>
                  </div>
                </div>
                {mockup.is_favorite && (
                  <div className="absolute top-2 right-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-text-dark truncate mb-1">{mockup.name}</h3>
                <p className="text-sm text-body-grey mb-3 capitalize">{mockup.template_name.replace(/-/g, ' ')}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(mockup.created_at), 'MMM d, yyyy')}
                  </span>
                  <span className="flex items-center">
                    <Download className="w-3 h-3 mr-1" />
                    {mockup.downloads}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mockup
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Downloads
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mockups.map((mockup) => (
                <tr key={mockup.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img 
                        src={mockup.thumbnail_url || mockup.mockup_url} 
                        alt={mockup.name}
                        className="w-10 h-10 rounded object-cover"
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 flex items-center">
                          {mockup.name}
                          {mockup.is_favorite && (
                            <Star className="w-4 h-4 text-yellow-500 fill-current ml-2" />
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 capitalize">
                      {mockup.template_name.replace(/-/g, ' ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {format(new Date(mockup.created_at), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatFileSize(mockup.file_size)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{mockup.downloads}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setPreviewMockup(mockup)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Preview"
                      >
                        <Maximize2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(mockup)}
                        className="text-brand-purple hover:text-brand-purple/80"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(mockup)}
                        className={`${mockup.is_favorite ? 'text-red-500' : 'text-gray-400'} hover:text-red-600`}
                        title="Toggle Favorite"
                      >
                        <Heart className={`w-4 h-4 ${mockup.is_favorite ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        onClick={() => handleDelete(mockup)}
                        className="text-red-600 hover:text-red-800"
                        disabled={isDeleting === mockup.id}
                        title="Delete"
                      >
                        {isDeleting === mockup.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {mockups.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileImage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No mockups found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery ? 'Try adjusting your search terms' : 'Create your first mockup to see it here'}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              size="medium"
              onClick={() => window.location.href = '/dashboard/mockups'}
            >
              Create Your First Mockup
            </Button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* More Options Modal */}
      {selectedMockup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedMockup(null)}
        >
          <div 
            className="bg-white rounded-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mockup Options</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  handleDownload(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Download Mockup</span>
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(selectedMockup.mockup_url);
                  alert('Link copied to clipboard!');
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Copy className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Copy Link</span>
              </button>
              <button
                onClick={() => {
                  handleRegenerateVariation(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
                <span className="text-gray-700">Generate Variation</span>
              </button>
              <button
                onClick={() => {
                  handleDelete(selectedMockup);
                  setSelectedMockup(null);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-lg transition-colors text-red-600"
              >
                <Trash2 className="w-5 h-5" />
                <span>Delete Mockup</span>
              </button>
            </div>
            <button
              onClick={() => setSelectedMockup(null)}
              className="mt-4 w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewMockup && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setPreviewMockup(null);
            setZoomLevel(1);
            setRotation(0);
          }}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewMockup(null);
                setZoomLevel(1);
                setRotation(0);
              }}
              className="absolute top-4 right-4 p-2 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-colors z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Image container */}
            <div 
              className="relative overflow-auto max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={previewMockup.mockup_url} 
                alt={previewMockup.name}
                className="max-w-full max-h-full object-contain transition-transform duration-300"
                style={{
                  transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                  cursor: zoomLevel > 1 ? 'move' : 'default'
                }}
              />
            </div>

            {/* Control bar */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(prev => Math.max(0.5, prev - 0.25));
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-5 h-5 text-white" />
              </button>
              
              <span className="text-white text-sm min-w-[60px] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel(prev => Math.min(3, prev + 0.25));
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-5 h-5 text-white" />
              </button>

              <div className="w-px h-6 bg-white/30" />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRotation(prev => (prev + 90) % 360);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Rotate"
              >
                <RotateCw className="w-5 h-5 text-white" />
              </button>

              <div className="w-px h-6 bg-white/30" />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(previewMockup);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Mockup info */}
            <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-md rounded-lg px-4 py-2">
              <h3 className="text-white font-semibold">{previewMockup.name}</h3>
              <p className="text-white/80 text-sm">
                {previewMockup.width && previewMockup.height && 
                  `${previewMockup.width} × ${previewMockup.height} • `
                }
                {formatFileSize(previewMockup.file_size)}
              </p>
            </div>

            {/* Keyboard hints */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md rounded-lg px-4 py-2 text-white/80 text-sm">
              <kbd className="px-2 py-1 bg-white/20 rounded">ESC</kbd> Close • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">+/-</kbd> Zoom • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">R</kbd> Rotate • 
              <kbd className="px-2 py-1 bg-white/20 rounded ml-2">D</kbd> Download
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Library;